import { supabase } from '../lib/supabase';

// שכבת הנתונים של צ'אט המאמנת: שליחת הודעה ל-/api/coach ושמירה/טעינה של ההיסטוריה
// מטבלת litebite_chat (RLS בעלים-בלבד). role מאוחסן בשמות קצרים: 'user' | 'coach'.

// כמו ב-services/ai.ts — בלי טיימאאוט בצד לקוח, תקלת רשת אמיתית תתקע את הצ'אט ללא הגבלה.
const AI_TIMEOUT_MS = 45000;

export type ChatRole = 'user' | 'coach';

export interface ChatMessage {
  role: ChatRole;
  content: string;
}

/** גבולות היום המקומי (לא UTC!) — כמו localDayRange ב-entries.ts. נשלח לשרת להקשר "היום". */
function localDayRange(date = new Date()): { from: string; to: string } {
  const from = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const to = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * שולח את היסטוריית השיחה ל-/api/coach ומחזיר את תשובת המאמנת.
 * ההודעה האחרונה במערך חייבת להיות של המשתמשת (השאלה החדשה). שגיאות — Error עם קוד מסודר.
 */
export async function sendCoachMessage(messages: ChatMessage[]): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');

  const { from, to } = localDayRange();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), AI_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch('/api/coach', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ messages, context: { day_start: from, day_end: to } }),
      signal: ctrl.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw new Error('ai_timeout');
    throw e;
  } finally {
    clearTimeout(timer);
  }
  const body = (await res.json().catch(() => null)) as { ok?: boolean; reply?: string; error?: string } | null;
  if (!res.ok || !body?.ok || !body.reply) {
    throw new Error(body?.error ?? `http_${res.status}`);
  }
  return body.reply;
}

/** היסטוריית הצ'אט מהמסד (ישן→חדש). שגיאה מוחזרת כ-Error; ריק זה תקין. */
export async function fetchChatHistory(limit = 50): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from('litebite_chat')
    .select('role, content')
    .order('created_at', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return ((data ?? []) as Array<{ role: string; content: string }>).map((r) => ({
    role: r.role === 'coach' ? 'coach' : 'user',
    content: r.content,
  }));
}

/** מחיקת כל היסטוריית הצ'אט של המשתמש */
export async function clearChat(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const { error } = await supabase.from('litebite_chat').delete().eq('user_id', session.user.id);
  if (error) throw error;
}

/** שומר הודעה בודדת בהיסטוריה (RLS דורש user_id=auth.uid()). כשל כאן לא-קריטי — הצ'אט עובד גם בלי. */
export async function saveChatMessage(role: ChatRole, content: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  const { error } = await supabase
    .from('litebite_chat')
    .insert({ user_id: session.user.id, role, content });
  if (error) throw error;
}
